import React, { useState } from "react";
import { ComposableMap, Geographies, Geography, Line, Marker, ZoomableGroup } from "react-simple-maps";
import { Paper, IconButton, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import FullscreenOutlinedIcon from "@mui/icons-material/FullscreenOutlined";
import MapToolTip from "./MapToolTip";
import ToolTipCards from "./ToolTipCards";
import Loading from "../Loading";
import { ClassNames } from "@emotion/react";
import { makeStyles } from "@material-ui/core/styles";

const useStyles = makeStyles({
  Paper: {
    height: "50vh",
    background: "#FFF",
    border: "0px !important",
    borderRadius: "8px !important",
    boxShadow: "none !important",
    // [theme.breakpoints.down('sm')]: {
    //   width: '100%',
    //   height:"58vh",
    // },
  },
  loader: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "58vh",
    width: "100%",
    position: "relative",
    top: 0,
    left: 0,
  },
  noData: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "58vh",
    width: "100%",
  },
  map: {
    width: "100%",
    height: "100%",
    borderRadius: "8px !important",
  },
  Marker: {
    cursor: "pointer",
    filter:
      "drop-shadow(0px 3.897px 3.118px rgba(41, 52, 149, 0.06)) drop-shadow(0px 9.365px 7.492px rgba(41, 52, 149, 0.08)) drop-shadow(0px 17.634px 14.107px rgba(41, 52, 149, 0.10)) drop-shadow(0px 31.456px 25.165px rgba(41, 52, 149, 0.13)) drop-shadow(0px 58.836px 47.069px rgba(41, 52, 149, 0.15)) drop-shadow(0px 140.831px 112.665px rgba(41, 52, 149, 0.21)) !important",
  },
  compass: {
    position: "absolute",
    top: "36.5vh",
    right: "30px",
  },
  mapIcon: {
    position: "absolute",
    top: "45vh",
    right: "30px",
    display: "flex",
    flexDirection: "row",
    borderRadius: "8px",
    background: "rgba(255, 255, 255, 0.80)",
    boxShadow: "0px 1px 2px 0px rgba(0, 0, 0, 0.05), 0px 2px 16px 0px rgba(0, 0, 0, 0.10)",
    minWidth: "100px",
    padding: "0px 4px",
    alignItems: "center",
    gap: "6px",
  },
  zoomIcon: {
    fontSize: "18px !important",
    color: "#333333",
  },
  fitIcon: {
    fontSize: "20px !important",
    color: "#333333",
  },
  verticalBar: {
    background: "rgba(0, 0, 0, 0.10)",
    width: "1px",
    height: "24px",
  },
});

const geoUrl = "https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json";

const Compass = () => {
  return (
    <svg xmlns='http://www.w3.org/2000/svg' width='46' height='47' viewBox='0 0 46 47' fill='none'>
      <path
        d='M34.5007 11.4998C28.1493 5.14838 17.8516 5.14838 11.5002 11.4998C5.14882 17.8512 5.14882 28.1488 11.5002 34.5002C17.8516 40.8516 28.1493 40.8516 34.5007 34.5002C40.8521 28.1488 40.8521 17.8512 34.5007 11.4998Z'
        stroke='#929497'
        strokeWidth='1.5'
      />
      <path
        d='M19.2044 18.281L21.443 13.8037C22.1225 12.4448 22.4622 11.7653 23.0002 11.7653C23.5382 11.7653 23.878 12.4448 24.5574 13.8037L26.7961 18.281C27.9539 20.5967 28.5328 21.7545 28.5328 23C28.5328 24.2455 27.9539 25.4033 26.7961 27.719L24.5574 32.1963C23.878 33.5552 23.5382 34.2347 23.0002 34.2347C22.4622 34.2347 22.1225 33.5552 21.443 32.1963L19.2044 27.719C18.0465 25.4033 17.4676 24.2455 17.4676 23C17.4676 21.7545 18.0465 20.5967 19.2044 18.281Z'
        stroke='#929497'
        strokeWidth='1.5'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
      <path
        d='M28.5334 23.0004C28.5334 21.7549 27.9545 20.5971 26.7966 18.2814L24.558 13.8041C23.8785 12.4452 23.5388 11.7657 23.0008 11.7657C22.4628 11.7657 22.1231 12.4452 21.4436 13.8041L19.2049 18.2814C18.0471 20.5971 17.4682 21.7549 17.4682 23.0004'
        stroke='#359DD0'
        strokeWidth='1.5'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
      <path d='M23 23L23 23.0146' stroke='#929497' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
    </svg>
  );
};

const MapChart = ({ chartData, MapLoading, _Module }) => {
  const [position, setPosition] = useState({ coordinates: [-96, 37.5], zoom: 0.5 });
  const [openToolTip, setOpenToolTip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const [locationName, setLocationName] = useState("");
  // const [mapWidth, setMapWidth] = useState(window.innerWidth);
  const mapWidth = window.innerWidth;
  // const [scale, setScale] = useState(2200);
  const classes = useStyles();
  const handleZoomIn = () => {
    if (position.zoom >= 4) { return;}
    setPosition((pos) => ({ ...pos, zoom: pos.zoom * 1.5 }));
  };

  const handleZoomOut = () => {
    if (position.zoom <= 0.3) { return;}
    setPosition((pos) => ({ ...pos, zoom: pos.zoom / 1.5 }));
  };

  const handleMoveEnd = (position) => {
    setPosition(position);
  };

  const handleMarkerClick = (name, event) => {
    event.stopPropagation();
    setOpenToolTip(!openToolTip);
    if (!openToolTip) {
      setLocationName(name);
    } else {
      setLocationName("");
    }

    const mapBounds = document.querySelector(".rsm-svg").getBoundingClientRect();
    const markerBounds = document.getElementById(name)?.getBoundingClientRect();
    if (markerBounds) {
      const top = markerBounds.top - mapBounds.top;
      const left = markerBounds.left - mapBounds.left + markerBounds.width / 2;
      setTooltipPosition({ top, left });
    }
  };

  const handleFitToScreen = () => {
    setPosition({ ...position, coordinates: [-96, 37.5], zoom: 1 });
    // setScale(1200);
  };

  const getProjectionScale = () => {
    if (mapWidth >= 1600) {
      return 2200;
    } else if (mapWidth >= 1200) {
      return 1800;
    } else if (mapWidth >= 600) {
      return 1600;
    } else {
      return 1400;
    }
  };

  const handleMapClick = () => {
    if (openToolTip) {
      setOpenToolTip(false);
      setLocationName("");
    }
  };

  return (
    <Paper elevation={0} className={classes.Paper}>
      {MapLoading ? (
        <div className={classes.loader}>
          <Loading />
        </div>
      ) : (
        <>
          {chartData?.length === 0 ? (
            <div className={classes.noData}>
              <Typography variant='h6' color='textSecondary'>
                No Data Available
              </Typography>
            </div>
          ) : (
            <>
              {window.location.pathname === '/ff' ?
                <ComposableMap projection='geoAlbersUsa' projectionConfig={{ scale: getProjectionScale() }} className={classes.map} onClick={handleMapClick}>
                  <ZoomableGroup center={position.coordinates} zoom={position.zoom} onMoveEnd={handleMoveEnd}>
                    <Geographies geography={geoUrl}>
                      {({ geographies }) =>
                        geographies.map((geo) => (
                          <Geography
                            key={geo.rsmKey}
                            geography={geo}
                            fill='#0067A1'
                            stroke='#333'
                            style={{
                              default: { outline: "none" },
                              hover: { outline: "none" },
                              pressed: { outline: "none" },
                            }}
                          />
                        ))
                      }
                    </Geographies>

                    {/* Draw Lines */}
                    {chartData?.map((item, index) => {
                      const { sourcecoordinates, Destinationcoordinates, LocationName } = item;
                      if (!sourcecoordinates || !Destinationcoordinates) { return null;}

                      return (
                        <Line
                          key={index}
                          from={sourcecoordinates}
                          to={Destinationcoordinates}
                          stroke="black"
                          strokeWidth={1}
                          strokeLinecap="round"
                          strokeDasharray="4 2"
                          onClick={(event) => handleMarkerClick(LocationName, event)}
                        />
                      );
                    })}

                    {/* Draw Markers */}
                    {chartData?.map((item, index) => {
                      const { sourcecoordinates, Destinationcoordinates, LocationName } = item;
                      return (
                        <>
                          <Marker key={`source-${index}`} coordinates={sourcecoordinates}
                            onClick={(event) => handleMarkerClick(LocationName, event)}
                          >
                            <circle
                              r={15}
                              fill={LocationName === locationName ? "#296989" : "#FF595D"}
                              stroke={LocationName === locationName ? "#359DD0" : "#F3AAAD"}
                              strokeWidth={8}
                              className={classes.Marker} />
                          </Marker>
                          <Marker key={`dest-${index}`} coordinates={Destinationcoordinates}
                            onClick={(event) => handleMarkerClick(LocationName, event)}
                          >

                            <circle
                              r={15}
                              fill={LocationName === locationName ? "#296989" : "#FF595D"}
                              stroke={LocationName === locationName ? "#359DD0" : "#F3AAAD"}
                              strokeWidth={8}
                              className={classes.Marker} />
                          </Marker>
                        </>
                      );
                    })}
                  </ZoomableGroup>
                </ComposableMap>
                :
                <ComposableMap projection='geoAlbersUsa' projectionConfig={{ scale: getProjectionScale() }} className={classes.map} onClick={handleMapClick}>
                  <ZoomableGroup center={position.coordinates} zoom={position.zoom} onMoveEnd={handleMoveEnd}>
                    <Geographies geography={geoUrl}>
                      {({ geographies }) =>
                        geographies.map((geo) => (
                          <Geography
                            key={geo.rsmKey}
                            geography={geo}
                            fill='#0067A1'
                            stroke='#333'
                            style={{
                              default: {
                                outline: "none",
                              },
                              hover: {
                                outline: "none",
                              },
                              pressed: {
                                outline: "none",
                              },
                            }}
                          />
                        ))
                      }
                    </Geographies>

                    {chartData?.map((item, index) => {
                      const { coordinates, LocationName } = item;
                      if (!Array.isArray(coordinates) || coordinates.some(coord => coord === null)) {
                        console.warn(`Invalid coordinates for ${LocationName}`, coordinates);
                        return null;
                      }

                      return (
                        <Marker
                          key={index}
                          coordinates={coordinates}
                          id={LocationName}
                          onClick={(event) => handleMarkerClick(LocationName, event)}
                        >
                          <circle
                            r={15}
                            fill={LocationName === locationName ? "#296989" : "#FF595D"}
                            stroke={LocationName === locationName ? "#359DD0" : "#F3AAAD"}
                            strokeWidth={8}
                            className={classes.Marker}
                          />
                        </Marker>
                      );
                    })}
                  </ZoomableGroup>
                </ComposableMap>}

              <div className={classes.compass}>
                <Compass />
              </div>
              <Paper className={classes.mapIcon}>
                <IconButton onClick={handleZoomIn}>
                  <AddIcon className={classes.zoomIcon} />
                </IconButton>
                <div className={classes.verticalBar} />
                <IconButton onClick={handleZoomOut}>
                  <RemoveIcon className={classes.zoomIcon} />
                </IconButton>
                <div className={classes.verticalBar} />
                <IconButton onClick={handleFitToScreen}>
                  <FullscreenOutlinedIcon className={classes.fitIcon} />
                </IconButton>
              </Paper>
              {openToolTip && (
                <MapToolTip
                  name={locationName}
                  data={chartData}
                  style={{
                    position: "absolute",
                    top: tooltipPosition.top,
                    left: tooltipPosition.left,
                    zIndex: 9999,
                    margin: "28px",
                    padding: "10px",
                    width: "200px",
                    backgroundColor: "#fff",
                    color: "rgba(0, 0, 0, 0.87)",
                    boxShadow: "0px 4px 12px 0px #0000001A",
                    borderRadius: "8px",
                    filter:
                      "drop-shadow(0px 3.897px 3.118px rgba(41, 52, 149, 0.06)) drop-shadow(0px 9.365px 7.492px rgba(41, 52, 149, 0.08)) drop-shadow(0px 17.634px 14.107px rgba(41, 52, 149, 0.10)) drop-shadow(0px 31.456px 25.165px rgba(41, 52, 149, 0.13)) drop-shadow(0px 58.836px 47.069px rgba(41, 52, 149, 0.15)) drop-shadow(0px 140.831px 112.665px rgba(41, 52, 149, 0.21))",
                  }}
                />
              )}
              <ToolTipCards open={openToolTip} name={locationName} data={chartData} tooltipPosition={tooltipPosition} />
            </>
          )}
        </>
      )}
    </Paper>
  );
};

export default MapChart;
